import { differenceInMinutes, parse } from 'date-fns'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import moment from 'moment'
import DOMPurify from 'dompurify'
import { ReactComponent as TrackingApprove } from '../images/trackingApprove.svg'
import { ReactComponent as TrackingReject } from '../images/CancelTrack.svg'
import { ReactComponent as TrackingPending } from '../images/Pending.svg'

export const formatDateParse = (item) => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  const dateString = new Date(item)
  const year = dateString.getFullYear()
  const monthIndex = dateString.getMonth()
  const monthName = months[monthIndex]
  const date = dateString.getDate()
  return `${date} ${monthName} ${year}`
}

export const isNotEmpty = (val) => !(val === undefined || val == null || val.length <= 0)

export const SearchUrlCustomQuery = () => {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}

export const getFormattedDate = (dateParam, withHours = false) => {
  const date = typeof dateParam === 'object' ? dateParam : new Date(dateParam);

  return withHours ? moment(date).format('DD MMMM YYYY - h:mm A') : moment(date)?.format('D MMMM YYYY');
}

export const getTimeAgo = (dateParam) => moment(dateParam).fromNow()

export const getFirstAndSecondCharacterOnly = (text) => {
  if (!text) return 'u' // (u) is a  default value

  const allNames = text?.trim()?.split(' ')

  if (allNames?.length > 1) {
    return `${allNames[0][0]}${allNames[allNames.length - 1][0]}`
  }
  return `${text[0]}`
}

export const objNotEmpty = (val) => isNotEmpty(val) && Object.keys(val).length > 0

export const addToArrIfNotExistOrRemove = (arr, findBy, newObj) => {
  const idExist = arr.find((singleItem) => singleItem[findBy] === newObj[findBy])
  let newArr = []

  if (isNotEmpty(idExist)) {
    newArr = arr.filter((singleItem) => singleItem[findBy] !== newObj[findBy])
  } else {
    newArr = [...arr, newObj]
  }

  return newArr
}

export const calculateTimeTaken = (startDate, endDate) => {
  const format = 'dd MMMM yyyy - hh:mm aa';
  const start = parse(startDate, format, new Date());
  const end = parse(endDate, format, new Date());

  const diffMs = differenceInMinutes(end, start);
  const diffHrs = Math.floor(diffMs / 60);
  const diffMins = diffMs % 60;

  return `${diffHrs}h ${diffMins}m`;
};

export const getFormattedDateWithDay = (date) => moment(date)?.format('dddd D MMMM YYYY - hh:mm A')
export const getFormattedDateWithoutDay = (date) => moment(date)?.format('D MMMM YYYY - hh:mm A')
export const getFormattedDateWithoutHours = (date) => moment(date)?.format('dddd D MMMM YYYY')
export const getFormattedDateHours = (date) => moment(date)?.format('hh:mm A')

export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export const throttle = (func, limit) => {
  let lastFunc;
  let lastRan;
  return (...args) => {
    if (!lastRan) {
      func(...args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if ((Date.now() - lastRan) >= limit) {
          func(...args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
};

export const decodeHtmlString = (encodedString) => (
  <p
    dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(encodedString),
    }}
  />
)

export const getFormatDate = (key, date) => {
  switch (key?.toLowerCase()) {
    case 'earlier': return getTimeAgo(date)
    case 'today': return getTimeAgo(date)
    case 'yesterday': return getFormattedDate(date, true)?.split('-')[1]
    default: return getFormattedDate(date, true)
  }
}

export const updateStateFromQueries = (state, queries) => {
  const initialStateKeys = Object.keys(state)
  Object.keys(queries).forEach((key) => {
    if (initialStateKeys.includes(key)) {
      state[key] = queries[key]
    }
  })
}

export const createArrayOfNumbers = (size) => Array.from({ length: size }, (_, i) => i);

export const getStatusColor = (status, translations) => {
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'approve':
      return { status: translations?.Approved, color: '#29CC8B', icon: <TrackingApprove /> }
    case 'sent':
      return { status: translations?.Sent, color: '#29CC8B', icon: <TrackingApprove /> }
    case 'approving':
      return { status: translations?.Approving, color: '#29CC8B', icon: <TrackingApprove /> }
    case 'pending':
    case 'inprogress':
      return { status: translations?.Pending, color: '#FFC542', icon: <TrackingPending /> }
    case 'rejected':
      return { status: translations?.Rejected, color: '#FC5A5A', icon: <TrackingReject /> }
    case 'cancelled':
      return { status: translations?.Canceled, color: '#336FFF', icon: <TrackingReject /> }
    case 'canceled':
      return { status: translations?.Canceled, color: '#336FFF', icon: <TrackingReject /> }
    case 'rejecting':
      return { status: translations?.Rejecting, color: '#FC5A5A', icon: <TrackingReject /> }
    case 'Valid Location':
      return { status: translations?.['Valid location'], color: '#29CC8B' }
    case 'unassigned location':
      return { status: translations?.['Unassigned Location'], color: '#FFC542' }
    case 'invalid location':
      return { status: translations?.['Invalid location'], color: '#FC5A5A' }
    default:
      return { status: '', color: '#29CC8B', icon: null }
  }
}

export const capitalizeFirstChart = (val) => val.charAt(0)
  .toUpperCase() + val.slice(1)
  .toLowerCase()
